import { 
  Heading, 
  Image, 
  Text, 
  VStack, 
  Box, 
  Badge,
  Wrap,
  WrapItem,
  HStack,
  Button,
  Icon,
  Flex,
  AspectRatio,
  Tag,
  TagLabel
} from "@chakra-ui/react";
import React from "react";
import PropTypes from 'prop-types';
import { FaGithub, FaExternalLinkAlt, FaFileAlt, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const Card = ({ 
  title, 
  description, 
  image,
  technologies, 
  demoLink, 
  githubLink, 
  dissertationLink, 
  category, 
  categories,
  id, 
  onClick
}) => {
  
  // Use categories if available, otherwise fall back to single category for backward compatibility
  const displayCategories = categories || (category ? [category] : []);
  
  const handleRealClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  return (
    <MotionBox 
      bg="brand.bg.card"
      borderRadius="xl"
      overflow="hidden"
      cursor="pointer"
      onClick={onClick ? handleRealClick : () => { }}
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-8px)",
        boxShadow: "xl",
      }}
      height="100%"
      position="relative"
      boxShadow="md"
      backdropFilter="blur(8px)"
      border="1px solid"
      borderColor="whiteAlpha.200"
      whileHover={{ y: -8 }}
    >
      {/* Image Container with Overlay */}
      <AspectRatio ratio={16/9} w="100%">
        <Box position="relative" w="100%" overflow="hidden">
          <Image 
            src={image}
            alt={title} 
            w="full"
            h="full"
            objectFit="cover" 
            transition="transform 0.5s ease"
            _groupHover={{ transform: "scale(1.05)" }}
          />
          <Box 
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0) 60%)"
          />
          
          {/* Category Badges */}
          {displayCategories.length > 0 && (
            <Flex 
              position="absolute"
              top="4"
              right="4"
              flexWrap="wrap"
              justifyContent="flex-end"
              gap={2}
            >
              {displayCategories.map((cat, index) => (
                <Badge 
                  key={index}
                  py={1.5}
                  px={3}
                  borderRadius="full"
                  colorScheme="blue"
                  fontSize="xs"
                  fontWeight="medium"
                  textTransform="uppercase"
                  letterSpacing="0.5px"
                  bg="rgba(49, 130, 206, 0.85)"
                  backdropFilter="blur(4px)"
                >
                  {cat}
                </Badge>
              ))}
            </Flex>
          )}
          
          {/* Title Overlay */}
          <VStack
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            p={4}
            align="flex-start"
            spacing={1}
          >
            <Heading 
              as="h3" 
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="bold"
              color="white"
              noOfLines={2}
              textShadow="0 2px 4px rgba(0,0,0,0.3)"
            >
              {title}
            </Heading>
          </VStack>
        </Box>
      </AspectRatio>

      {/* Content Section */}
      <VStack 
        spacing={4}
        align="start" 
        p={5}
        h="100%"
      >
        <Text 
          color="brand.text.secondary"
          fontSize="md"
          lineHeight="1.7"
        >
          {description}
        </Text>

        {/* Technology Tags */}
        {technologies && technologies.length > 0 && (
          <Wrap spacing={3} mt={2} mb={-1}>
            {technologies.map((tech, index) => (
              <WrapItem key={index}>
                <Tag
                  size="lg"
                  borderRadius="full"
                  variant="subtle"
                  colorScheme="blue"
                  bg="whiteAlpha.200"
                  py={2}
                  px={4}
                >
                  <TagLabel fontWeight="medium" fontSize="sm">{tech}</TagLabel>
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {/* Action Button and External Links in one row */}
        <Flex 
          mt={2} 
          w="full" 
          justify="space-between"
          align="center"
        >
          {/* Action Button */}
          <MotionFlex 
            whileHover={{ x: 5 }}
            transition={{ duration: 0.2 }}
          >
            <Button
              variant="ghost"
              size="sm"
              rightIcon={<Icon as={FaArrowRight} />}
              colorScheme="blue"
              fontWeight="medium"
              _hover={{
                bg: "whiteAlpha.200",
              }}
              onClick={handleRealClick}
            >
              View Details
            </Button>
          </MotionFlex>
          
          {/* External Links */}
          <HStack 
            spacing={3} 
            opacity="0.8"
            wrap="wrap"
            justify="flex-end"
          >
            {demoLink && (
              <Button
                as="a"
                href={demoLink}
                target="_blank"
                leftIcon={<Icon as={FaExternalLinkAlt} />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={handleLinkClick}
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Live Demo
              </Button>
            )}
            {githubLink && (
              <Button
                as="a"
                href={githubLink}
                target="_blank"
                leftIcon={<Icon as={FaGithub} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={handleLinkClick}
                _hover={{ bg: "whiteAlpha.200" }}
              >
                GitHub
              </Button>
            )}
            {dissertationLink && (
              <Button
                as="a"
                href={dissertationLink}
                target="_blank"
                leftIcon={<Icon as={FaFileAlt} />}
                size="sm"
                variant="ghost"
                colorScheme="purple"
                onClick={handleLinkClick}
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Dissertation
              </Button>
            )}
          </HStack>
        </Flex>
      </VStack>
    </MotionBox>
  );
};

Card.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.string,
  technologies: PropTypes.arrayOf(PropTypes.string),
  demoLink: PropTypes.string,
  githubLink: PropTypes.string,
  dissertationLink: PropTypes.string,
  category: PropTypes.string,
  categories: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  onClick: PropTypes.func,
};

Card.defaultProps = {
  description: "",
  image: "",
  technologies: [],
  demoLink: null,
  githubLink: null,
  dissertationLink: null,
  category: null,
  categories: null,
  id: null,
  onClick: null,
};

export default Card;
